import * as React from "react";
import { ApiError, type LoginResponse, type MeResponse, type UserPublic } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Public login input for the super-admin portal — only what the user
 * types. The tenant slug is the hardcoded `_super_admin` value
 * supplied by the API client's `tenantSlugProvider`, never typed.
 */
export interface PortalLoginInput {
  email: string;
  password: string;
}

type AuthStatus = "loading" | "authed" | "anonymous";

interface AuthState {
  status: AuthStatus;
  user: UserPublic | null;
}

interface AuthContextValue extends AuthState {
  login: (input: PortalLoginInput) => Promise<UserPublic>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

/**
 * Cross-portal cookie defense (companion to the IA fix landed alongside
 * this commit). The eos_refresh cookie is scoped to .eduviaos.com so it
 * gets replayed on every subdomain — including sa-stage. If somehow an
 * IA admin's cookie was minted and they then visited sa-stage, the BE
 * refresh would 401 (it now binds the cookie to the request tenant
 * slug, which the SA SPA hardcodes to "_super_admin"). This client-side
 * guard is defense-in-depth against a brief deployment skew where the
 * BE fix isn't live yet: only treat the session as authed when the
 * user actually carries the super_admin role.
 */
function isSuperAdmin(user: UserPublic): boolean {
  return user.role === "super_admin";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState>({
    status: "loading",
    user: null,
  });

  /** Silent refresh on mount via HttpOnly cookie. */
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refreshed = await api.post<{ access_token: string; expires_in: number }>(
          "/auth/refresh",
        );
        api.setAccessToken(refreshed.access_token);
        const me = await api.get<MeResponse>("/auth/me");
        if (cancelled) return;
        if (!isSuperAdmin(me.user)) {
          // Wrong-portal cookie — drop it server-side too, then bounce
          // to login. RequireAuth will handle the redirect once we set
          // anonymous below.
          api.setAccessToken(null);
          try {
            await api.post("/auth/logout");
          } catch {
            // ignore — BE refresh already refuses the cookie
          }
          setState({ status: "anonymous", user: null });
          return;
        }
        setState({ status: "authed", user: me.user });
      } catch {
        if (!cancelled) setState({ status: "anonymous", user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = React.useCallback(async (input: PortalLoginInput) => {
    // The shared API client injects X-Tenant-Slug = "_super_admin"
    // (hardcoded in lib/api.ts). We never put tenant_id in the body
    // for the super-admin surface — the slug is platform-fixed.
    const resp = await api.post<LoginResponse>("/auth/login", {
      email: input.email,
      password: input.password,
    });
    if (!isSuperAdmin(resp.user)) {
      // Non-SA logging in via the SA portal — refuse loud rather than
      // letting the SPA mount with a privilege mismatch.
      throw new ApiError("This account is not authorised for the Super Admin portal.", {
        status: 403,
        code: "NOT_SUPER_ADMIN",
      });
    }
    api.setAccessToken(resp.tokens.access_token);
    setState({ status: "authed", user: resp.user });
    return resp.user;
  }, []);

  const logout = React.useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (!(err instanceof ApiError) || err.status >= 500) {
        console.warn("logout request failed", err);
      }
    }
    api.setAccessToken(null);
    setState({ status: "anonymous", user: null });
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
